import { createOrderOnline, updateOrderStatusOnline } from "@/features/orders/api";
import type { OrderInput, OrderStatus } from "@/features/orders/types";
import { createPaymentOnline } from "@/features/payments/api";
import type { PaymentInput } from "@/features/payments/types";
import { useOfflineQueue } from "@/shared/offline/offlineQueue";
import type { OfflineMutation } from "@/shared/offline/offlineQueue";

/** Mutations that keep failing are dropped after this many replay attempts so one bad entry cannot block the queue forever. */
const MAX_ATTEMPTS = 5;

let flushing = false;

async function replay(mutation: OfflineMutation): Promise<void> {
  switch (mutation.type) {
    case "order.create":
      // mutation.id is the idempotency key generated when the mutation was queued,
      // so a replay whose response was lost cannot double-write on the next attempt.
      await createOrderOnline(mutation.payload as OrderInput, mutation.id);
      return;
    case "order.status":
      // Naturally idempotent — setting the same status twice is a no-op.
      await updateOrderStatusOnline(mutation.payload as { orderId: string; status: OrderStatus });
      return;
    case "payment.create":
      await createPaymentOnline(mutation.payload as PaymentInput, mutation.id);
      return;
  }
}

export type FlushResult = {
  synced: number;
  failed: number;
  dropped: number;
};

/**
 * Replays queued offline mutations in FIFO order. Stops early if the device
 * goes back offline. Safe to call repeatedly — concurrent calls no-op.
 */
export async function flushOfflineQueue(): Promise<FlushResult> {
  const result: FlushResult = { synced: 0, failed: 0, dropped: 0 };

  if (flushing || !navigator.onLine) return result;
  flushing = true;

  try {
    const store = useOfflineQueue.getState();
    if (!store.hydrated) {
      await store.hydrate();
    }

    // Snapshot; entries enqueued during the flush are picked up on the next run.
    const pending = [...useOfflineQueue.getState().queue];

    for (const mutation of pending) {
      if (!navigator.onLine) break;

      try {
        await replay(mutation);
        await useOfflineQueue.getState().remove(mutation.id);
        result.synced += 1;
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown sync error";

        if (mutation.attempts + 1 >= MAX_ATTEMPTS) {
          await useOfflineQueue.getState().remove(mutation.id);
          result.dropped += 1;
        } else {
          await useOfflineQueue.getState().markFailed(mutation.id, message);
          result.failed += 1;
        }
      }
    }
  } finally {
    flushing = false;
  }

  return result;
}
